// V9.25 · Orçamento mensal: teto por categoria e alerta quando o gasto passa do planejado
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Target, Trash2 } from 'lucide-react';
import api from '@/lib/api';

const money = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const STATUS = {
  ok: ['No planejado', 'bg-emerald-100 text-emerald-800 border-emerald-300', 'bg-emerald-500'],
  atencao: ['Perto do teto', 'bg-amber-100 text-amber-800 border-amber-300', 'bg-amber-500'],
  estourado: ['Estourou', 'bg-rose-100 text-rose-800 border-rose-300', 'bg-rose-500'],
};

export const OrcamentoTab = ({ competencia, planoContas = [] }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ cod_conta: '', valor_teto: '', escopo: 'padrao' });

  const despesas = useMemo(
    () => planoContas.filter((c) => c.tipo === 'DESPESA' && c.ativo !== false), [planoContas]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/fin/ledger/budgets', { params: { competencia } });
      setData(d);
    } catch (_) { toast.error('Erro ao carregar o orçamento.'); }
    finally { setLoading(false); }
  }, [competencia]);

  useEffect(() => { load(); }, [load]);

  const salvar = async () => {
    if (!form.cod_conta) return toast.error('Selecione a categoria.');
    if (!form.valor_teto || Number(form.valor_teto) <= 0) return toast.error('Informe o teto.');
    try {
      await api.put('/fin/ledger/budgets', {
        cod_conta: form.cod_conta,
        valor_teto: Number(String(form.valor_teto).replace(',', '.')),
        competencia: form.escopo === 'mes' ? (competencia || data?.competencia) : null,
      });
      toast.success('Teto salvo.');
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar o teto.'); }
  };

  const excluir = async (i) => {
    try {
      await api.delete(`/fin/ledger/budgets/${i.id}`);
      toast.success('Teto removido.'); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao remover.'); }
  };

  const t = data?.totais;

  return (
    <div className="space-y-3" data-testid="tab-orcamento">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Competência <strong>{data?.competencia}</strong> · teto padrão vale para todo mês; o teto do mês
          sobrepõe o padrão
        </p>
        <Button onClick={() => { setForm({ cod_conta: '', valor_teto: '', escopo: 'padrao' }); setOpen(true); }}
          data-testid="orc-novo"><Plus className="w-4 h-4 mr-1" /> Definir teto</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="rounded-xl border p-3" data-testid="orc-teto-total">
          <p className="text-[11px] font-bold uppercase text-muted-foreground">Teto planejado</p>
          <p className="text-xl font-bold tabular-nums">{money(t?.teto)}</p>
        </div>
        <div className="rounded-xl border p-3" data-testid="orc-gasto">
          <p className="text-[11px] font-bold uppercase text-muted-foreground">Gasto no teto</p>
          <p className="text-xl font-bold tabular-nums">{money(t?.gasto)}</p>
        </div>
        <div className={`rounded-xl border p-3 ${(t?.restante ?? 0) < 0 ? 'border-rose-300 bg-rose-50/60' : 'border-emerald-300 bg-emerald-50/50'}`}
          data-testid="orc-restante">
          <p className="text-[11px] font-bold uppercase text-muted-foreground">Saldo do orçamento</p>
          <p className="text-xl font-bold tabular-nums">{money(t?.restante)}</p>
          <p className="text-[11px] text-muted-foreground">{t?.percent ?? 0}% consumido</p>
        </div>
        <div className="rounded-xl border p-3 border-amber-300 bg-amber-50/60" data-testid="orc-estourados">
          <p className="text-[11px] font-bold uppercase text-muted-foreground">Categorias estouradas</p>
          <p className="text-xl font-bold tabular-nums">{t?.estourados ?? 0}</p>
        </div>
        <div className="rounded-xl border p-3" data-testid="orc-sem-teto">
          <p className="text-[11px] font-bold uppercase text-muted-foreground">Gasto sem teto</p>
          <p className="text-xl font-bold tabular-nums">{money(t?.sem_teto)}</p>
          <p className="text-[11px] text-muted-foreground">de {money(t?.despesa_total_mes)} no mês</p>
        </div>
      </div>

      {!!data?.alertas?.length && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="orc-alertas">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700" /> Avisos do orçamento ({data.alertas.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {data.alertas.map((a, i) => (
              <p key={i} className="text-sm flex items-start gap-2" data-testid={`orc-alerta-${i}`}>
                <Badge variant="outline" className={a.nivel === 'danger'
                  ? 'bg-rose-100 text-rose-800 border-rose-300' : 'bg-amber-100 text-amber-800 border-amber-300'}>
                  {a.nivel === 'danger' ? 'Estourou' : 'Atenção'}
                </Badge>
                <span>{a.mensagem}</span>
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4" /> Tetos por categoria</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Categoria</TableHead><TableHead>Escopo</TableHead>
              <TableHead className="text-right">Teto</TableHead><TableHead className="text-right">Gasto</TableHead>
              <TableHead className="text-right">Saldo</TableHead><TableHead className="w-40">Consumo</TableHead>
              <TableHead>Situação</TableHead><TableHead>Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="text-center py-8">Carregando…</TableCell></TableRow>}
              {!loading && !data?.itens?.length && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                  Nenhum teto definido. Clique em "Definir teto" para começar a controlar o planejado.
                </TableCell></TableRow>
              )}
              {!loading && (data?.itens || []).map((i) => (
                <TableRow key={i.id} data-testid={`orc-row-${i.cod_conta}`}>
                  <TableCell>
                    <p className="font-medium">{i.descricao}</p>
                    <p className="text-[11px] text-muted-foreground font-mono">
                      {i.cod_conta}{i.classe ? ` · ${i.classe}` : ''}
                    </p>
                  </TableCell>
                  <TableCell className="text-xs">{i.padrao ? 'Todo mês' : i.competencia}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(i.valor_teto)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(i.gasto)}</TableCell>
                  <TableCell className={`text-right tabular-nums font-medium ${i.restante < 0 ? 'text-rose-700' : ''}`}>
                    {money(i.restante)}
                  </TableCell>
                  <TableCell>
                    <div className="h-2 w-32 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${STATUS[i.status][2]}`}
                        style={{ width: `${Math.min(100, i.percent)}%` }} />
                    </div>
                    <span className="text-[11px] tabular-nums">{i.percent}%</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS[i.status][1]}
                      data-testid={`orc-status-${i.cod_conta}`}>{STATUS[i.status][0]}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" className="text-red-600"
                      onClick={() => excluir(i)} data-testid={`orc-excluir-${i.cod_conta}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-orcamento">
          <DialogHeader>
            <DialogTitle>Definir teto de gasto</DialogTitle>
            <DialogDescription>
              Avisamos assim que o gasto da categoria passar de 80% e quando estourar o planejado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Categoria de despesa *</Label>
              <Select value={form.cod_conta} onValueChange={(v) => setForm({ ...form, cod_conta: v })}>
                <SelectTrigger data-testid="orc-categoria"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {despesas.map((c) => (
                    <SelectItem key={c.cod_conta} value={c.cod_conta}>
                      {c.cod_conta} · {c.descricao}{c.classe ? ` (${c.classe})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Teto mensal (R$) *</Label>
                <Input type="number" step="0.01" value={form.valor_teto}
                  onChange={(e) => setForm({ ...form, valor_teto: e.target.value })}
                  data-testid="orc-valor" />
              </div>
              <div>
                <Label>Vale para</Label>
                <Select value={form.escopo} onValueChange={(v) => setForm({ ...form, escopo: v })}>
                  <SelectTrigger data-testid="orc-escopo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="padrao">Todo mês (padrão)</SelectItem>
                    <SelectItem value="mes">Só {data?.competencia}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={salvar} data-testid="orc-salvar">Salvar teto</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrcamentoTab;
